import * as React from 'react'
import * as ReactRedux from 'react-redux'
import * as ReactRouter from 'react-router-dom'

import * as Herz from '@rushplay/herz'
import * as Common from '@rushplay/common'
import { lazy as loadable } from '@loadable/component'

import * as Theming from './theming'
import * as Configuration from './configuration'
import { Card } from './card'
import { HtmlContent } from './html-content'
import { NavigationLink, PageNavigation } from './page-navigation'
import { createFileFromString } from './create-file-from-string'

const NotFound = loadable(() => import('./not-found'))

export function TermsPage() {
  const params = ReactRouter.useParams()
  const page = params?.slug
  const contentRef = React.useRef(null)

  const translate = Herz.I18n.useTranslate(
    () => [
      `terms-and-condition.${page}.content`,
      `terms-and-condition.${page}.download`,
    ],
    [page]
  )

  if (!translate(`terms-and-condition.${page}.content`)) {
    return <NotFound />
  }

  return (
    <Theming.Alternative>
      <Common.Box pt={0} flexGrow={[null, '1']} display={[null, 'flex']}>
        <Common.Box
          display="flex"
          p={[1, null, 4]}
          justifyContent="center"
          alignItems="flex-start"
          fontSize={2}
          minHeight="100%"
          flexGrow={[null, '1']}
        >
          <Card p={[2, 4]} width="100%" maxWidth={[null, null, null, '1000px']}>
            <Common.Space bottom={3}>
              <Common.Box ref={contentRef}>
                <HtmlContent
                  html={{
                    __html: translate(`terms-and-condition.${page}.content`),
                  }}
                />
              </Common.Box>
            </Common.Space>

            <Common.Box
              onClick={() =>
                createFileFromString(
                  contentRef.current.innerText,
                  `bitdreams-${page}-terms-and-conditions`
                )
              }
              style={{
                margin: 'auto',
                width: 'fit-content',
                cursor: 'pointer',
                textDecoration: 'underline',
                padding: '8px',
              }}
            >
              {translate(`terms-and-condition.${page}.download`)}
            </Common.Box>
          </Card>
        </Common.Box>
      </Common.Box>
    </Theming.Alternative>
  )
}

export function TermsAndConditions() {
  const translate = Herz.I18n.useTranslate()
  const isHorseRacingEnabled = ReactRedux.useSelector(state =>
    Configuration.isHorseRacingEnabled(state.configuration)
  )

  return (
    <Common.Box
      flexGrow="1"
      maxWidth={['100vw', '100%']}
      pt={[1, 2]}
      display={[null, 'flex']}
      flexDirection={[null, 'column']}
    >
      <Common.Box>
        <PageNavigation header={translate('terms-and-conditions')}>
          <React.Fragment>
            <NavigationLink to="/terms-and-conditions/general">
              {translate('terms-and-conditions.nav.general')}
            </NavigationLink>

            {translate('terms-and-conditions.nav.your-account') && (
              <NavigationLink to="/terms-and-conditions/your-account">
                {translate('terms-and-conditions.nav.your-account')}
              </NavigationLink>
            )}

            {translate('terms-and-conditions.nav.welcome-offer') && (
              <NavigationLink to="/terms-and-conditions/welcome-offer">
                {translate('terms-and-conditions.nav.welcome-offer')}
              </NavigationLink>
            )}

            {translate('terms-and-conditions.nav.horse-racing') &&
              isHorseRacingEnabled && (
                <NavigationLink to="/terms-and-conditions/horse-racing">
                  {translate('terms-and-conditions.nav.horse-racing')}
                </NavigationLink>
              )}

            {translate('terms-and-conditions.nav.mahjong') && (
              <NavigationLink to="/terms-and-conditions/mahjong">
                {translate('terms-and-conditions.nav.mahjong')}
              </NavigationLink>
            )}
          </React.Fragment>
        </PageNavigation>
      </Common.Box>
      <ReactRouter.Switch>
        <ReactRouter.Redirect
          exact
          from="/terms-and-conditions"
          to="terms-and-conditions/general"
        />
        <ReactRouter.Route
          path="/terms-and-conditions/:slug"
          component={TermsPage}
        />
      </ReactRouter.Switch>
    </Common.Box>
  )
}

Herz.I18n.Loader.preload(
  [
    'terms-and-conditions.nav.general',
    'terms-and-conditions.nav.your-account',
    'terms-and-conditions.nav.horse-racing',
    'terms-and-conditions.nav.welcome-offer',
    'terms-and-conditions.nav.sports',
    'terms-and-conditions.nav.mahjong',
    'terms-and-conditions.general.download',
    'terms-and-conditions.general.content',
    'terms-and-conditions',
  ],
  TermsAndConditions
)

export default TermsAndConditions
